import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { inventoryService } from '../../../services/inventoryService';
import { calculateAllocation } from '../../../utils/lotAllocation';
import type { InventoryItem } from '../../../types/inventory';
import { useLanguage } from '../../../features/language/useLanguage';
import { basicInfoSchema, type BasicInfoFormData } from '../schemas/workOrderSchema';
import { buildLotOptionName, type LotOption } from '../hooks/workOrderReferenceData';
import type { LotRow } from '../hooks/useWorkOrderForm';
import { evaluateBulkCard, type BulkCardIssue } from './bulkCardIssues';

const STOCK_EPSILON = 0.0001;

export interface BulkCardSnapshot {
  /** lotId -> total weight this card allocates to it. Feeds the cross-card budget. */
  allocations: Record<string, number>;
  isComplete: boolean;
  /** Blocks the whole batch from scheduling; empty for an untouched card. */
  issues: BulkCardIssue[];
}

export interface BulkCardCollected {
  /** null when react-hook-form validation failed; the card shows its own field errors. */
  basicInfo: BasicInfoFormData | null;
  itemId: string;
  lots: { itemId: string; lotId: string; quantity: number }[];
  inputWeight: number;
  outputWeight: number;
  roastType: string;
  isComplete: boolean;
}

export interface WorkOrderCardFormDefaults {
  machine?: string;
  roaster?: string;
  machineTypeId?: string;
  roasterId?: string;
  targetDate?: string;
  time?: string;
}

const emptyLotRow = (): LotRow => ({ lotId: '', remaining: 0, usedWeight: undefined });

const sumUsedWeight = (rows: LotRow[]) =>
  rows.reduce((sum, row) => {
    const numeric = Number(row.usedWeight || 0);
    return Number.isFinite(numeric) ? sum + numeric : sum;
  }, 0);

export const useWorkOrderCardForm = ({
  defaults,
  greenBeans,
  otherAllocations,
}: {
  defaults?: WorkOrderCardFormDefaults;
  greenBeans: InventoryItem[];
  /** lotId -> weight already taken by the OTHER cards in this batch. */
  otherAllocations: Record<string, number>;
}) => {
  const { t } = useLanguage();

  const {
    register,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<BasicInfoFormData>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      machine: defaults?.machine || '',
      roaster: defaults?.roaster || '',
      machineTypeId: defaults?.machineTypeId || '',
      roasterId: defaults?.roasterId || '',
      targetDate: defaults?.targetDate || new Date().toISOString().split('T')[0],
      time: defaults?.time || new Date().toTimeString().slice(0, 5),
      internalNotes: '',
    },
  });

  const [selectedBeanId, setSelectedBeanId] = useState('');
  const [lots, setLots] = useState<LotRow[]>([emptyLotRow()]);
  const [manualInputWeight, setManualInputWeight] = useState('');
  const [manualOutputWeight, setManualOutputWeight] = useState('');
  const [roastType, setRoastType] = useState('');
  const [isAutoCalculatingLots, setIsAutoCalculatingLots] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  // Keyed on the bean, so two cards on the same bean share one request.
  const lotQuery = useQuery({
    queryKey: ['work-order-lots', selectedBeanId],
    queryFn: () => inventoryService.getLots(selectedBeanId),
    enabled: !!selectedBeanId,
    staleTime: 60 * 1000,
  });

  const lotOptions = useMemo<LotOption[]>(() => {
    const fetched = lotQuery.data ?? [];
    const bean = greenBeans.find((b) => b.id === selectedBeanId);
    const profiles = bean?.greenBeanProfiles || [];

    return fetched
      .map((lot) => {
        // A lot's green bean profile is linked by lot_id; its `type`
        // discriminator is not reliably 'LotType', so match by lot_id.
        const profile = profiles.find((p) => p.lot_id === lot.id);
        // The cross-card budget: what the other cards in this batch already
        // claimed is not available to this one, even though the server still
        // holds it. Without this, two cards happily over-draw one lot and the
        // second PUT fails with ErrInsufficientLotStock — after the first one
        // already deducted stock.
        const budgeted = Math.max(lot.remainingQuantity - (otherAllocations[lot.id] ?? 0), 0);
        return {
          id: lot.id,
          name: buildLotOptionName(lot.lotNumber, profile, budgeted, t),
          remaining: budgeted,
          density: profile?.density,
          moisture: profile?.moisture_content,
          processType: profile?.process_type ?? null,
          hasGreenBeanProfile: !!profile,
          greenBeanProfileId: profile?.id,
        };
      })
      .filter((lot) => lot.remaining > STOCK_EPSILON || lots.some((row) => row.lotId === lot.id));
  }, [lotQuery.data, greenBeans, selectedBeanId, otherAllocations, lots, t]);

  // A row stores the lot's remaining stock at the moment it was picked. When
  // another card in the batch later claims part of the same lot the budget
  // shrinks underneath this one, so re-sync the stored figure — otherwise the
  // insufficient-stock check below would still be measuring against the old
  // number and would wave the over-draw through.
  useEffect(() => {
    setLots((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (!row.lotId) return row;
        const match = lotOptions.find((l) => l.id === row.lotId);
        if (!match || match.remaining === row.remaining) return row;
        changed = true;
        return { ...row, remaining: match.remaining };
      });
      return changed ? next : prev;
    });
  }, [lotOptions]);

  const applyTotalToInputWeight = useCallback((rows: LotRow[]) => {
    const rounded = Number(sumUsedWeight(rows).toFixed(3));
    setManualInputWeight(rounded > 0 ? rounded.toString() : '');
  }, []);

  const handleBeanChange = useCallback((value: string) => {
    setSelectedBeanId(value);
    setLots([emptyLotRow()]);
    setManualInputWeight('');
  }, []);

  const handleLotChange = useCallback(
    (index: number, lotId: string) => {
      const selected = lotOptions.find((l) => l.id === lotId);
      const updated = [...lots];
      const parsedTarget = Number(manualInputWeight || 0);
      const targetWeight = Number.isFinite(parsedTarget) ? parsedTarget : 0;
      const otherRowsWeight = updated.reduce((sum, row, idx) => {
        if (idx === index) return sum;
        const numeric = Number(row.usedWeight || 0);
        return Number.isFinite(numeric) ? sum + numeric : sum;
      }, 0);
      const remainingTarget = Math.max(targetWeight - otherRowsWeight, 0);
      const remainingInLot = Number(selected?.remaining || 0);
      const autoUsedWeight =
        lotId && remainingTarget > 0 && remainingInLot > 0
          ? Number(Math.min(remainingTarget, remainingInLot).toFixed(3))
          : undefined;

      updated[index] = {
        ...updated[index],
        lotId,
        remaining: selected?.remaining || 0,
        usedWeight: autoUsedWeight,
      };
      setLots(updated);
    },
    [lotOptions, lots, manualInputWeight],
  );

  const handleWeightChange = useCallback(
    (index: number, value: number | undefined) => {
      const updated = [...lots];
      updated[index] = { ...updated[index], usedWeight: value };
      setLots(updated);
      applyTotalToInputWeight(updated);
    },
    [lots, applyTotalToInputWeight],
  );

  const addLotRow = useCallback(() => setLots((prev) => [...prev, emptyLotRow()]), []);

  const removeLotRow = useCallback(
    (index: number) => {
      const updated = lots.filter((_, i) => i !== index);
      setLots(updated);
      applyTotalToInputWeight(updated);
    },
    [lots, applyTotalToInputWeight],
  );

  const handleWeightInputChange = useCallback((val: number | undefined) => {
    setManualInputWeight(val === undefined ? '' : val.toString());
  }, []);

  const handleWeightOutputChange = useCallback((val: number | undefined) => {
    setManualOutputWeight(val === undefined ? '' : val.toString());
  }, []);

  const handleAutoCalculateLots = useCallback(() => {
    if (!selectedBeanId || !manualInputWeight) return;
    const weight = Number(manualInputWeight);
    if (!Number.isFinite(weight) || weight <= 0) return;

    try {
      setIsAutoCalculatingLots(true);
      const result = calculateAllocation(
        weight,
        lotOptions.map((l) => ({ id: l.id, name: l.name, remaining: l.remaining })),
      );
      if (result.warning) setWarningMessage(result.warning);
      if (result.lots.length > 0) {
        setLots(result.lots.map((l) => ({ ...l, usedWeight: Number(l.usedWeight) })));
      }
    } catch (error) {
      console.error(t('workOrders.form.errors.autoCalculateLotsFailed'), error);
      setWarningMessage(t('workOrders.form.errors.allocationErrorOccurred'));
    } finally {
      setIsAutoCalculatingLots(false);
    }
  }, [selectedBeanId, manualInputWeight, lotOptions, t]);

  const validLots = useMemo(
    () => lots.filter((l) => l.lotId && Number(l.usedWeight || 0) > 0),
    [lots],
  );

  const totalUsedWeight = sumUsedWeight(lots);
  const parsedRoastInputWeight = Number(manualInputWeight || 0);
  const parsedRoastOutputWeight = Number(manualOutputWeight || 0);
  const hasRoastInputWeight = Number.isFinite(parsedRoastInputWeight) && parsedRoastInputWeight > 0;
  const hasLotsUsedWeight = validLots.length > 0;

  const isWeightMismatch =
    hasRoastInputWeight && hasLotsUsedWeight && Math.abs(totalUsedWeight - parsedRoastInputWeight) > 0;

  const isOutputExceedsInput =
    parsedRoastInputWeight > 0 &&
    parsedRoastOutputWeight > 0 &&
    parsedRoastOutputWeight > parsedRoastInputWeight;

  const hasInsufficientStock = validLots.some(
    (l) => Number(l.usedWeight || 0) > l.remaining + STOCK_EPSILON,
  );

  const { isComplete, issues } = useMemo(
    () =>
      evaluateBulkCard({
        selectedBeanId,
        validLotCount: validLots.length,
        roastType,
        inputWeight: parsedRoastInputWeight,
        outputWeight: parsedRoastOutputWeight,
        hasInsufficientStock,
        isOutputExceedsInput,
      }),
    [
      selectedBeanId,
      validLots.length,
      roastType,
      parsedRoastInputWeight,
      parsedRoastOutputWeight,
      hasInsufficientStock,
      isOutputExceedsInput,
    ],
  );

  const snapshot = useMemo<BulkCardSnapshot>(() => {
    const allocations: Record<string, number> = {};
    for (const row of validLots) {
      allocations[row.lotId] = (allocations[row.lotId] ?? 0) + Number(row.usedWeight || 0);
    }
    return { allocations, isComplete, issues };
  }, [validLots, isComplete, issues]);

  const collect = useCallback(async (): Promise<BulkCardCollected> => {
    const valid = await trigger();
    return {
      basicInfo: valid ? getValues() : null,
      itemId: selectedBeanId,
      lots: validLots.map((l) => ({
        itemId: selectedBeanId,
        lotId: l.lotId,
        quantity: Number(l.usedWeight || 0),
      })),
      inputWeight: parsedRoastInputWeight,
      outputWeight: parsedRoastOutputWeight,
      roastType,
      isComplete,
    };
  }, [
    trigger,
    getValues,
    selectedBeanId,
    validLots,
    parsedRoastInputWeight,
    parsedRoastOutputWeight,
    roastType,
    isComplete,
  ]);

  return {
    register,
    control,
    setValue,
    errors,
    /** Reads the current basic-info values without triggering validation, so a
     *  freshly added card can inherit machine/roaster/date/time from this one. */
    getValues,
    /** Drives the machine min/max input hint, same as the single form. */
    selectedMachineTypeId: watch('machineTypeId'),
    selectedBeanId,
    lots,
    lotOptions,
    manualInputWeight,
    manualOutputWeight,
    roastType,
    setRoastType,
    isAutoCalculatingLots,
    warningMessage,
    setWarningMessage,
    handleBeanChange,
    handleLotChange,
    handleWeightChange,
    addLotRow,
    removeLotRow,
    handleWeightInputChange,
    handleWeightOutputChange,
    handleAutoCalculateLots,
    totalUsedWeight,
    parsedRoastInputWeight,
    isWeightMismatch,
    isOutputExceedsInput,
    isComplete,
    snapshot,
    collect,
  };
};
